'use client';

import { fetchEtlSponsors } from '@/layers/presentation/services/etlSponsors';
import { orderSponsorsForDirectory } from '@/layers/seo/sponsorDirectory';
import logger from '@/layers/support/monitoring/logger';
import type { EtlSponsor } from '@/types/etl';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const LOG_MODULE = 'FE [Components / Directory / SponsorsList]';

interface SponsorsListProps {
  /**
   * Server-rendered listing. When it is present the browser fetch is skipped,
   * so these links and the page's ItemList JSON-LD come from one array.
   */
  initialSponsors?: Pick<EtlSponsor, 'id' | 'name'>[];
}

export default function SponsorsList({ initialSponsors }: SponsorsListProps) {
  const [sponsors, setSponsors] = useState<Pick<EtlSponsor, 'id' | 'name'>[]>(
    initialSponsors ?? [],
  );
  const [loading, setLoading] = useState(!initialSponsors);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialSponsors) return;

    async function loadSponsors() {
      try {
        logger.debug(`${LOG_MODULE} loadSponsors: begin`);
        const data = await fetchEtlSponsors();
        setSponsors(data);
        logger.debug(`${LOG_MODULE} loadSponsors: end`, { count: data.length });
      } catch (err) {
        logger.error(`${LOG_MODULE} loadSponsors: error`, { err });
        setError('Failed to load sponsors');
      } finally {
        setLoading(false);
      }
    }

    loadSponsors();
  }, [initialSponsors]);

  if (loading) {
    return <div className="py-12 text-center text-default-600">Loading sponsors…</div>;
  }

  if (error) {
    return <div className="py-12 text-center text-danger">{error}</div>;
  }

  const rows = orderSponsorsForDirectory(sponsors);

  const groups: Record<string, { id: number; name: string }[]> = {};
  for (const s of rows) {
    const ch = s.name[0]?.toUpperCase() || '#';
    const letter = ch >= 'A' && ch <= 'Z' ? ch : '#';
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(s);
  }
  const letters = Object.keys(groups).sort((a, b) =>
    a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b),
  );

  return (
    <>
      <div className="mb-8 flex flex-wrap gap-2">
        {letters.map(letter => (
          <Link
            key={letter}
            href={`#${letter}`}
            className="rounded-full bg-default-200 px-3 py-1.5 text-default-700 transition-colors duration-200 hover:bg-default-300 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {letter}
          </Link>
        ))}
      </div>

      {letters.map(letter => (
        <section key={letter} id={letter} className="scroll-mt-24 mb-12">
          <h2 className="mb-3">{letter}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-4 !list-none">
            {groups[letter].map(s => (
              <li key={s.id}>
                <Link href={`/directory/${s.id}`} className="text-primary underline">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
